import React, { createContext, useContext, useState, useCallback } from 'react';

interface ToastMessage {
  id: string;
  type: 'success' | 'error' | 'info' | 'warning';
  message: string;
}

interface ToastContextType {
  toast: (message: string, type?: 'success' | 'error' | 'info' | 'warning') => void;
}

const ToastContext = createContext<ToastContextType>({ toast: () => {} });

export function useToast() {
  return useContext(ToastContext);
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);

  const toast = useCallback((message: string, type: 'success' | 'error' | 'info' | 'warning' = 'success') => {
    const id = Math.random().toString(36).slice(2);
    setToasts(prev => [...prev, { id, type, message }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 4000);
  }, []);

  const colors = {
    success: { bg: '#f0fdf4', border: '#86efac', color: '#166534', icon: '\u2713' },
    error: { bg: '#fef2f2', border: '#fca5a5', color: '#991b1b', icon: '\u2715' },
    info: { bg: '#eff6ff', border: '#93c5fd', color: '#1e40af', icon: '\u2139' },
    warning: { bg: '#fffbeb', border: '#fcd34d', color: '#92400e', icon: '\u26A0' },
  };

  return (
    <ToastContext.Provider value={{ toast }}>
      {children}
      {/* Toast container */}
      <div
        aria-live="polite"
        aria-label="Notifications"
        style={{
          position: 'fixed',
          top: 20,
          right: 20,
          zIndex: 10000,
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          maxWidth: 380,
        }}
      >
        {toasts.map(t => {
          const c = colors[t.type];
          return (
            <div
              key={t.id}
              role="status"
              style={{
                padding: '12px 16px',
                background: c.bg,
                border: `1px solid ${c.border}`,
                borderRadius: 10,
                color: c.color,
                fontSize: 13,
                fontWeight: 500,
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                animation: 'slideIn 0.3s ease-out',
              }}
            >
              <span style={{ fontSize: 16, fontWeight: 700 }}>{c.icon}</span>
              <span style={{ flex: 1 }}>{t.message}</span>
              <button
                onClick={() => setToasts(prev => prev.filter(x => x.id !== t.id))}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: c.color, fontSize: 14, padding: '2px 4px' }}
                aria-label="Dismiss notification"
              >
                ×
              </button>
            </div>
          );
        })}
      </div>
      <style>{`
        @keyframes slideIn {
          from { opacity: 0; transform: translateX(20px); }
          to { opacity: 1; transform: translateX(0); }
        }
      `}</style>
    </ToastContext.Provider>
  );
}
